/**
 * Phase 9 (Phase 20: per campaign group) — the auto-send facts in the
 * operator's words, pure: the country's one line for the header strip (how
 * many groups send, today's sends), each group's line for its row, their
 * tone. Times are the recipients' clock with the Danish echo beside.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenGroupSendStatus, DropGenSchedulerPlanStatus, DropGenServerSendModes } from '@trafficpulse/shared';

import type { DropGenPlanDetail } from '../../../drop-generator.types';

export type AutoSendMode = 'real' | 'simulated' | 'off';

/** What switching a group to Live really does on THIS server — said before it happens. */
export const MODE_LINE: Record<AutoSendMode, string> = {
  real: 'From now on the engine sends each draft at its planned time — real SMS, real money.',
  simulated: 'This is a test server: a Live draft is marked sent at its time with made-up stats — nothing reaches SMSWiser.',
  off: 'This server never sends — Live is saved, and only a server that sends will send.',
};

/** Does this server send a Live draft? Unknown = yes (the API decides). */
export const runnable = (_g: DropGenGroupSendStatus, server: DropGenServerSendModes | undefined): boolean => !server || server.real || server.simulated;

/** " · made-up sends (test server)" — a Live group's sends on a server that makes them up. */
export const simulatedNote = (live: DropGenGroupSendStatus[], server?: DropGenServerSendModes): string =>
  live.length > 0 && server && !server.real && server.simulated ? ' · made-up sends (test server)' : '';

/** The server runs nothing at all (development without the simulation). */
export const serverSendsNothing = (server: DropGenServerSendModes | undefined) => Boolean(server && !server.real && !server.simulated);

export type AutoSendTone = 'off' | 'on' | 'shadow' | 'halted' | 'stuck' | 'blocked';

export interface AutoSendFact {
  tone: AutoSendTone;
  text: string;
}

export const n = (v: number) => v.toLocaleString('en-US');

/** "united_kingdom" → "United Kingdom". */
export const countryLabel = (key: string) =>
  key.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/** "14:30" on a time zone's clock (the recipients' or Copenhagen). */
export const clockIn = (iso: string, tz: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: tz });

/** Danish first (15/10/2026): "05:30 🇩🇰 · 14:30 local"; Danish alone without the country's timezone. */
export const dkThenLocal = (iso: string, tz: string | null) =>
  `${clockIn(iso, 'Europe/Copenhagen')} 🇩🇰${tz ? ` · ${clockIn(iso, tz)} local` : ''}`;

/** The country's line: Kill, the technical halt, then how many groups send. */
export function countryFact(
  plan: DropGenPlanDetail,
  status: DropGenSchedulerPlanStatus | undefined,
  groups: DropGenGroupSendStatus[],
  server: DropGenServerSendModes | undefined,
): AutoSendFact {
  if (plan.killSwitch) return { tone: 'blocked', text: 'stopped — the kill switch is on' };
  if (!plan.enabled) return { tone: 'blocked', text: 'stopped — the plan is disabled' };
  if (plan.autoSendHaltKind === 'TECHNICAL') return { tone: 'stuck', text: 'halted — a send needs a person' };
  if (groups.length === 0) return { tone: 'off', text: 'off — no campaign group has a master config yet' };
  const on = groups.filter((g) => g.config.autoSend);
  if (on.length === 0) return { tone: 'off', text: groups.length > 1 ? 'off in every campaign group — you send' : 'off — you send' };
  const real = on.filter((g) => !g.config.shadow);
  if (real.length > 0 && serverSendsNothing(server)) return { tone: 'blocked', text: 'live — this server never sends (development)' };
  const shadow = on.length - real.length;
  const halted = on.filter((g) => g.haltedToday).length;
  const parts = [modeWords(real.length, shadow, groups.length)];
  if (halted > 0) parts.push(`${halted} halted for today`);
  if (real.length > 0) parts.push(`${n(status?.sendToday?.sentDrops ?? 0)} sent today`);
  if (shadow > 0) parts.push(`${n(status?.sendToday?.shadowDrops ?? 0)} would have sent`);
  const next = nextOf(on.filter((g) => !g.haltedToday));
  if (next) parts.push(`next ${dkThenLocal(next, plan.recipientTimezone)}`);
  const tone: AutoSendTone = halted === on.length ? 'halted' : real.length === 0 ? 'shadow' : 'on';
  return { tone, text: `${parts.join(' · ')}${simulatedNote(real, server)}` };
}

/** "live in 1 of 3 campaign groups · simulated in 1" — or plainly "live" / "simulated". */
function modeWords(real: number, shadow: number, total: number): string {
  if (total === 1) return real > 0 ? 'live' : 'simulated';
  const parts = [];
  if (real > 0) parts.push(`live in ${real} of ${total} campaign groups`);
  if (shadow > 0) parts.push(real > 0 ? `simulated in ${shadow}` : `simulated in ${shadow} of ${total} campaign groups`);
  return parts.join(' · ');
}

/** One campaign group's line in the table. */
export function groupFact(g: DropGenGroupSendStatus, tz: string | null, server: DropGenServerSendModes | undefined): AutoSendFact {
  if (!g.config.autoSend) return { tone: 'off', text: 'off — you send' };
  const shadow = g.config.shadow;
  const madeUp = !shadow && Boolean(server && !server.real && server.simulated);
  if (g.haltedToday) return { tone: 'halted', text: shadow ? 'simulated — would have halted for today: its stop-loss matched' : 'halted for today — its stop-loss matched' };
  if (!shadow && !runnable(g, server)) return { tone: 'blocked', text: 'live — this server never sends; its drafts wait' };
  const today = g.sendToday;
  if (!today) return shadow ? { tone: 'shadow', text: 'simulated — waiting for today’s drafts' } : { tone: 'on', text: 'live — waiting for today’s drafts' };
  const parts = shadow ? ['simulated', `${n(today.shadowDrops)} would have sent`] : [`${n(today.sentDrops)} sent${madeUp ? ' (made up — test server)' : ''}`];
  if (today.nextAtUtc) parts.push(`next ${dkThenLocal(today.nextAtUtc, tz)}`);
  const cap = g.config.dailySmsCap;
  const spent = shadow ? today.sentSms + today.shadowSms : today.sentSms;
  parts.push(cap ? `${n(spent)} of ${n(cap)} SMS` : `${n(spent)} SMS`);
  return { tone: shadow ? 'shadow' : 'on', text: parts.join(' · ') };
}

/** The groups' next planned send (ISO), or null. */
export const nextOf = (groups: DropGenGroupSendStatus[]) =>
  groups
    .map((g) => g.sendToday?.nextAtUtc)
    .filter((x): x is string => Boolean(x))
    .sort()[0] ?? null;
